
import React, { useState } from "react";
import { StartupAccount } from "@/types/startup";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Newspaper, Plus, Clock, CheckCircle, XCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";

interface StartupNewsSubmissionsProps {
  startupAccount: StartupAccount;
}

const StartupNewsSubmissions: React.FC<StartupNewsSubmissionsProps> = ({ startupAccount }) => {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    category: "",
    external_link: ""
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.content.trim()) {
      toast({
        title: "Missing Information",
        description: "Please fill in title and content",
        variant: "destructive"
      });
      return;
    }

    try {
      setSubmitting(true);

      const { error } = await supabase
        .from("startup_news_submissions")
        .insert({
          startup_account_id: startupAccount.id,
          title: formData.title.trim(),
          content: formData.content.trim(),
          category: formData.category.trim() || null,
          external_link: formData.external_link.trim() || null
        });

      if (error) throw error;

      toast({
        title: "News Submitted",
        description: "Your news submission has been sent for review"
      });

      setFormData({ title: "", content: "", category: "", external_link: "" });
      setDialogOpen(false);
    } catch (error) {
      console.error("Error submitting news:", error);
      toast({
        title: "Error",
        description: "Failed to submit news",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">News Submissions</h2>
          <p className="text-muted-foreground">Submit stories for Teenvisor Startup News</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Submit News
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Submit News Story</DialogTitle>
              <DialogDescription>
                Share your startup's story with the teen community
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Story Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter story title"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="content">Story Content *</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Tell your story - startup journey, milestones, lessons learned..."
                  rows={5}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Input
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  placeholder="e.g., Funding, Product Launch, Team Building"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="external_link">External Link</Label>
                <Input
                  id="external_link"
                  type="url"
                  value={formData.external_link}
                  onChange={(e) => setFormData(prev => ({ ...prev, external_link: e.target.value }))}
                  placeholder="https://your-blog-post.com"
                />
              </div>
              
              <div className="flex gap-2">
                <Button type="submit" disabled={submitting} className="flex-1">
                  {submitting ? "Submitting..." : "Submit for Review"}
                </Button>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Newspaper className="h-5 w-5" />
            Submission Guidelines
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm">
            <h4 className="font-medium">What to Include:</h4>
            <ul className="list-disc list-inside text-muted-foreground mt-1 space-y-1">
              <li>Authentic startup stories and experiences</li>
              <li>Lessons learned and advice for teen entrepreneurs</li>
              <li>Product launches and milestones</li>
              <li>Behind-the-scenes insights</li>
            </ul>
          </div>
          
          <div className="text-sm">
            <h4 className="font-medium">Review Process:</h4>
            <div className="grid grid-cols-3 gap-2 mt-2">
              <div className="flex items-center gap-1 text-xs">
                <Clock className="h-3 w-3 text-yellow-500" />
                Pending Review
              </div>
              <div className="flex items-center gap-1 text-xs">
                <CheckCircle className="h-3 w-3 text-green-500" />
                Approved
              </div>
              <div className="flex items-center gap-1 text-xs">
                <XCircle className="h-3 w-3 text-red-500" />
                Rejected
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Your Submissions</CardTitle>
          <CardDescription>
            Track the status of your news submissions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-8">
            No submissions yet. Share your startup's story with the community!
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default StartupNewsSubmissions;
